import React, { useState, useEffect } from "react";
import {
  Modal,
  Form,
  Input,
  Select,
  Switch,
  message,
  Upload,
  Avatar,
  Row,
  Col,
  Button,
  Typography,
  Tooltip,
  Divider,
} from "antd";
import {
  UserOutlined,
  CameraOutlined,
  LockOutlined,
} from "@ant-design/icons";
import userService from "../../../../services/UserService";
import { useUser } from "../../../../context/UserContext";
import useAuth from "../../../../hooks/useAuth";

const { Option } = Select;
const { Text } = Typography;

const SectionLabel = ({ children }) => (
  <Text
    style={{
      display: "block",
      fontSize: 11,
      fontWeight: 600,
      letterSpacing: "0.06em",
      textTransform: "uppercase",
      color: "rgba(0,0,0,0.45)",
      marginBottom: 12,
    }}
  >
    {children}
  </Text>
);

const ModalEditarUsuario = ({ open, setOpen, item, fetchData }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [imageUrl, setImageUrl] = useState("");
  const [photoData, setPhotoData] = useState(null);
  const user = useUser();
  const { session } = useAuth();
  const isAdmin = session?.user?.role === "Administrador";

  const fileToBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result || "";
        const base64 = String(result).split(",")[1] || "";
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const loadTemporaryPhoto = async (userId) => {
    try {
      const data = await userService.obtenerUrlFotoUsuario(
        userId,
        user.idToken
      );
      if (data?.url) {
        setImageUrl(data.url);
      }
    } catch (error) {
      console.error("Error al cargar foto temporal:", error);
    }
  };

  useEffect(() => {
    if (open && item) {
      form.setFieldsValue({
        name: item.name,
        lastName: item.lastName,
        email: item.email,
        document: item.document,
        phone: item.phone,
        role: item.role,
        team: item.team,
        gender: item.gender,
        city: item.city,
        country: item.country,
        isActive: item.isActive,
      });
      setPhotoData(item.photo || null);
      setImageUrl("");
      if (item.photo?.path) {
        loadTemporaryPhoto(item.id);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, item, form]);

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const userData = {
        ...values,
        photo: photoData || item.photo,
      };

      await userService.EditarUsusairo(item.id, userData, user.idToken);
      message.success("Usuario actualizado correctamente");
      setOpen(false);
      form.resetFields();
      fetchData();
    } catch (error) {
      console.error("Error al actualizar usuario:", error);
      message.error("Error al actualizar el usuario");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setOpen(false);
    form.resetFields();
    setImageUrl("");
    setPhotoData(null);
  };

  const handleUploadPhoto = async (file) => {
    setUploadingPhoto(true);
    try {
      const base64 = await fileToBase64(file);
      const uploadResponse = await userService.subirFotoUsuario(
        {
          userId: item.id,
          file: base64,
          contentType: file.type,
          fileName: file.name,
        },
        user.idToken
      );

      if (uploadResponse?.photo) {
        setPhotoData(uploadResponse.photo);
      }

      const temp = await userService.obtenerUrlFotoUsuario(
        item.id,
        user.idToken
      );
      setImageUrl(temp?.url || URL.createObjectURL(file));
      message.success(`${file.name} subido correctamente`);
    } catch (error) {
      console.error("Error al subir foto:", error);
      message.error(`${file.name} falló al subir`);
    } finally {
      setUploadingPhoto(false);
    }

    return false;
  };

  const uploadProps = {
    name: "file",
    accept: "image/jpeg,image/jpg,image/png,image/webp,image/gif",
    showUploadList: false,
    beforeUpload: handleUploadPhoto,
  };

  return (
    <Modal
      title="Editar usuario"
      open={open}
      onCancel={handleCancel}
      footer={null}
      width={640}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{ isActive: true }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 16,
            padding: "4px 0 20px",
          }}
        >
          <Upload {...uploadProps} disabled={uploadingPhoto || !item?.id}>
            <div style={{ position: "relative", cursor: "pointer" }}>
              <Avatar
                size={64}
                src={imageUrl}
                icon={<UserOutlined />}
              />
              <div
                style={{
                  position: "absolute",
                  bottom: -2,
                  right: -2,
                  width: 24,
                  height: 24,
                  borderRadius: "50%",
                  background: "#7AC142",
                  border: "2px solid #fff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  opacity: uploadingPhoto ? 0.6 : 1,
                }}
              >
                <CameraOutlined style={{ fontSize: 11, color: "#fff" }} />
              </div>
            </div>
          </Upload>
          <div>
            <Text strong style={{ fontSize: 15 }}>
              {item?.name} {item?.lastName}
            </Text>
            <div>
              <Text type="secondary" style={{ fontSize: 13 }}>
                {item?.email}
              </Text>
            </div>
          </div>
        </div>

        <SectionLabel>Identidad</SectionLabel>
        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item
              name="name"
              label="Nombre"
              rules={[
                { required: true, message: "Por favor ingrese el nombre" },
              ]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              name="lastName"
              label="Apellido"
              rules={[
                { required: true, message: "Por favor ingrese el apellido" },
              ]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              name="document"
              label="Documento"
              rules={[
                { required: true, message: "Por favor ingrese el documento" },
              ]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              name="gender"
              label="Género"
              rules={[
                { required: true, message: "Por favor seleccione el género" },
              ]}
            >
              <Select>
                <Option value="M">Masculino</Option>
                <Option value="F">Femenino</Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Divider style={{ margin: "4px 0 20px" }} />

        <SectionLabel>Contacto</SectionLabel>
        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item
              name="email"
              label="Email"
              rules={[
                { required: true, message: "Por favor ingrese el email" },
                { type: "email", message: "Por favor ingrese un email válido" },
              ]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              name="phone"
              label="Teléfono"
              rules={[
                { required: true, message: "Por favor ingrese el teléfono" },
              ]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              name="city"
              label="Ciudad"
              rules={[
                { required: true, message: "Por favor ingrese la ciudad" },
              ]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              name="country"
              label="País"
              rules={[
                { required: true, message: "Por favor ingrese el país" },
              ]}
            >
              <Input />
            </Form.Item>
          </Col>
        </Row>

        <Divider style={{ margin: "4px 0 20px" }} />

        <SectionLabel>Rol y acceso</SectionLabel>
        <Row gutter={16} align="top">
          <Col xs={24} sm={12}>
            <Form.Item
              name="role"
              label={
                isAdmin ? (
                  "Rol"
                ) : (
                  <span>
                    Rol{" "}
                    <Tooltip title="Solo un administrador puede modificar el rol de un usuario">
                      <LockOutlined
                        style={{ marginLeft: 4, color: "rgba(0,0,0,0.35)" }}
                      />
                    </Tooltip>
                  </span>
                )
              }
              rules={[
                { required: true, message: "Por favor seleccione el rol" },
              ]}
            >
              <Select disabled={!isAdmin}>
                <Option value="Auxiliar de Operador de Estación">
                  Auxiliar de Operador de Estación
                </Option>
                <Option value="Operador de Estación">
                  Operador de Estación
                </Option>
                <Option value="Personal Policía Metro">
                  Personal Policía Metro
                </Option>
                <Option value="Personal Vigilancia Privada">
                  Personal Vigilancia Privada
                </Option>
                <Option value="Analista de Zona">Analista de Zona</Option>
                <Option value="Analista">Analista</Option>
                <Option value="Administrador">Administrador</Option>
                <Option value="Coordinador">Coordinador</Option>
                <Option value="Inspector">Inspector</Option>
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              name="team"
              label={
                isAdmin ? (
                  "Team"
                ) : (
                  <span>
                    Team{" "}
                    <Tooltip title="Solo un administrador puede modificar el team de un usuario">
                      <LockOutlined
                        style={{ marginLeft: 4, color: "rgba(0,0,0,0.35)" }}
                      />
                    </Tooltip>
                  </span>
                )
              }
              rules={[
                { required: true, message: "Por favor seleccione el team" },
              ]}
            >
              <Select disabled={!isAdmin}>
                <Option value="CISE">CISE</Option>
                <Option value="EMER">EMER</Option>
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              label="Estado de la cuenta"
              style={{ marginBottom: 0 }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  height: 32,
                }}
              >
                <Form.Item name="isActive" valuePropName="checked" noStyle>
                  <Switch
                    checkedChildren="Activo"
                    unCheckedChildren="Inactivo"
                  />
                </Form.Item>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Un usuario inactivo no podrá iniciar sesión
                </Text>
              </div>
            </Form.Item>
          </Col>
        </Row>

        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 8,
            marginTop: 24,
          }}
        >
          <Button onClick={handleCancel}>Cancelar</Button>
          <Button type="primary" htmlType="submit" loading={loading}>
            Guardar cambios
          </Button>
        </div>
      </Form>
    </Modal>
  );
};

export default ModalEditarUsuario;
